import React, { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, LogIn, ShieldCheck, ShieldAlert, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import ThemeToggle from "../components/ThemeToggle";
import BrandLogo from "../components/BrandLogo";
import SsoButtons from "../components/SsoButtons";
import MfaVerifyForm from "../components/MfaVerifyForm";
import MfaSettings from "../components/MfaSettings";

export default function Login() {
  const { user, login, completeMfaLogin, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("keith@unitedcloud.ca");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  // MFA challenge flow (after password/Google SSO succeeds for an account with MFA enrolled)
  const [mfaChallenge, setMfaChallenge] = useState(location.state?.mfaToken
    ? { mfaToken: location.state.mfaToken, methods: location.state.methods } : null);

  // Forced password change flow (after login when requires_password_change is true)
  const [mustChange, setMustChange] = useState(false);
  const [changingUser, setChangingUser] = useState(null);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew, setShowNew] = useState(false);

  // Forced MFA enrollment flow (grace period expired, no method enrolled yet)
  const [mustSetupMfa, setMustSetupMfa] = useState(false);

  // If a user session already exists but their password change is still pending
  // (e.g. they refreshed mid-flow), drop them straight into the update form.
  React.useEffect(() => {
    if (user?.requires_password_change && !mustChange) {
      setChangingUser(user);
      setMustChange(true);
    } else if (user?.mfa_setup_blocking && !mustSetupMfa) {
      setMustSetupMfa(true);
    }
  }, [user, mustChange, mustSetupMfa]);

  if (user && !user.requires_password_change && !mustChange && !mustSetupMfa) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setMfaChallenge({ mfaToken: result.mfaToken, methods: result.methods });
        return;
      }
      if (result.user?.requires_password_change) {
        setChangingUser(result.user);
        setCurrentPw(password);
        setMustChange(true);
        return;
      }
      if (result.user?.mfa_setup_blocking) {
        setMustSetupMfa(true);
        return;
      }
      navigate("/");
    } catch (err) {
      toast({ title: "Sign-in failed", description: err?.response?.data?.detail || "Please check your credentials.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const onMfaVerified = (accessToken, verifiedUser) => {
    completeMfaLogin(accessToken, verifiedUser);
    setMfaChallenge(null);
    if (verifiedUser?.requires_password_change) {
      setChangingUser(verifiedUser);
      setMustChange(true);
      return;
    }
    navigate("/");
  };

  const continueAfterMfaSetup = async () => {
    try {
      const { data } = await api.get("/auth/mfa/status");
      if (data.totp_enabled || data.passkeys.length > 0) {
        navigate("/");
      } else {
        toast({ title: "Set up at least one method first", description: "Add a passkey or authenticator app to continue.", variant: "destructive" });
      }
    } catch { /* non-fatal — button just won't navigate */ }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast({ title: "Password too short", description: "New password must be at least 8 characters.", variant: "destructive" });
      return;
    }
    if (newPw !== confirmPw) {
      toast({ title: "Passwords don't match", description: "Please retype the new password.", variant: "destructive" });
      return;
    }
    if (newPw === currentPw) {
      toast({ title: "Choose a different password", description: "The new password must differ from the temporary one.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast({ title: "Password updated", description: "You're all set. Welcome to UC Vigil." });
      await refresh();
      navigate("/");
    } catch (err) {
      toast({ title: "Couldn't update password", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen login-bg flex flex-col">
      <div className="absolute top-4 right-4 z-10"><ThemeToggle /></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo — UnitedCloud wordmark (theme-aware: swaps for light/dark) */}
          <div className="flex items-center justify-center mb-8">
            <BrandLogo variant="full" className="h-48 w-auto" data-testid="login-unitedcloud-logo" />
          </div>

          {mustSetupMfa ? (
            <div className="rounded-2xl border border-border bg-card shadow-xl shadow-primary/5 p-6 sm:p-8" data-testid="mfa-setup-required-card">
              <div className="flex items-center gap-2 text-destructive text-[12px] font-semibold uppercase tracking-wider">
                <ShieldAlert className="w-4 h-4" /> Action required
              </div>
              <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Set up two-factor authentication</h1>
              <p className="text-[13px] text-muted-foreground mt-1">
                Your admin requires MFA on this account. Add a passkey or authenticator app to continue.
              </p>
              <div className="mt-6"><MfaSettings /></div>
              <Button onClick={continueAfterMfaSetup} data-testid="mfa-setup-continue-button" className="w-full h-10 mt-5">Continue</Button>
            </div>
          ) : mfaChallenge ? (
            <MfaVerifyForm
              mfaToken={mfaChallenge.mfaToken}
              methods={mfaChallenge.methods}
              onVerified={onMfaVerified}
              onBack={() => setMfaChallenge(null)}
            />
          ) : !mustChange ? (
            <div className="rounded-2xl border border-border bg-card shadow-xl shadow-primary/5 p-6 sm:p-8" data-testid="login-card">
              <div className="flex items-center gap-2 text-primary text-[12px] font-semibold uppercase tracking-wider">
                <ShieldCheck className="w-4 h-4" /> Staff only
              </div>
              <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Sign in to your account</h1>
              <p className="text-[13px] text-muted-foreground mt-1">Use your UC Vigil staff credentials to continue.</p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-[12px] text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot?
                    </button>
                  </div>
                  <div className="relative">
                    <Input id="password" data-testid="login-password-input" type={show ? "text" : "password"} autoComplete="current-password"
                      value={password} onChange={(e) => setPassword(e.target.value)} required className="pr-10" />
                    <button type="button" onClick={() => setShow((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" data-testid="login-submit-button" disabled={busy} className="w-full h-10 gap-2">
                  <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              <SsoButtons />
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card shadow-xl shadow-primary/5 p-6 sm:p-8" data-testid="force-change-card">
              <div className="flex items-center gap-2 text-primary text-[12px] font-semibold uppercase tracking-wider">
                <KeyRound className="w-4 h-4" /> Update required
              </div>
              <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Set a new password</h1>
              <p className="text-[13px] text-muted-foreground mt-1">
                Welcome{changingUser?.name ? `, ${changingUser.name.split(" ")[0]}` : ""}. For security, please replace your temporary password before continuing.
              </p>

              <form onSubmit={onChangePassword} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="current-password">Current (temporary) password</Label>
                  <Input id="current-password" data-testid="current-password-input" type="password"
                    autoComplete="current-password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                    required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative">
                    <Input id="new-password" data-testid="new-password-input" type={showNew ? "text" : "password"}
                      autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                      required minLength={8} className="pr-10" />
                    <button type="button" onClick={() => setShowNew((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground">
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[11.5px] text-muted-foreground">Minimum 8 characters.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input id="confirm-password" data-testid="confirm-password-input" type={showNew ? "text" : "password"}
                    autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                    required minLength={8} />
                </div>
                <Button type="submit" data-testid="change-password-submit-button" disabled={busy} className="w-full h-10 gap-2">
                  <KeyRound className="w-4 h-4" /> {busy ? "Updating…" : "Update password & continue"}
                </Button>
              </form>
            </div>
          )}

          <div className="mt-6 text-center text-[11.5px] text-muted-foreground">
            © {new Date().getFullYear()} UC Vigil · Internal staff portal
          </div>
        </div>
      </div>
    </div>
  );
}
